<template>
    <div v-if="multipleChoicePromo && findUltraFastService()" class="buyflow-panel bg-gray-100 border border-gray-200 mb-10">
        <div>
            <AppModal ref="learnMoreModal">
                <template #contents>
                    <div class="text-base text-left mb-2">
                        {{ footnoteInfo }}
                    </div>
                </template>
            </AppModal>
        </div>

        <div v-if="showGiftChoicePopup">
            <AppModal 
                ref="giftChoiceModal"
                :fit-content="true"
                :border-radius="'rounded-3xl'"
                :has-borders="false"
                @dismiss="handleModalDismiss">
                <template #contents>
                    <div class="text-center py-10 desktop:px-16 desktop:y-10">
                        <h3 class="text-xl desktop:text-2xl font-bold mb-1">You must choose a Gift before proceeding.</h3>
                        <p class="mb-6">Click on a gift to proceed to the next step.</p>
                        <div class="flex justify-center space-x-4">
                            <button class="bg-blue text-white py-3 px-20 rounded-3xl hover:bg-blue-light"
                            @click.prevent="$refs.giftChoiceModal.dismiss()">
                                Close
                            </button>
                        </div>
                    </div>
                </template>
            </AppModal>
        </div>
        <!-- If there's an applied GiftChoice promotion -->
        <div v-if="hasMultipleChoicePromo && currentChoices.length > 0">
            <div class="promo-banner bg-salmon p-1 rounded-md">
                <h3 class="text-center text-base font-bold">&#9733; ONLY WITH ULTRAFAST AND GIGAFAST INTERNET PLANS</h3>
            </div>
        
            <h3 class="text-center text-xl font-bold pt-2">Pick your promo!</h3>
            
            <div class="p-4">
                
                <p class="center-text">Select an offer below and get <b>2 Months free</b> internet or a <b>prepaid gift card</b> with your UltraFast or GigaFast plan.</p>
                
                <div class="flex flex-reverse flex-wrap">
                    <div 
                        v-for="(option, index) in currentChoices" 
                        :key="option.key"
                        class="flex-1 basis-full sm:basis-1/2 px-2 mb-4 tablet:mb-0"
                    >
                        <div class="promo-option rounded-lg cursor-pointer transition-colors h-full bg-white border-2 border-gray-200 group"
                            :class="{ 'border-blue': selectedOption === option.key, 'border-gray-200 hover:border-blue': selectedOption !== option.key }"
                            @click="selectOption(option.key)">

                            <div class="promo-image-container">
                                <img 
                                    v-if="option.promoType === 'free_months'" 
                                    :class="[
                                        'w-full h-32 object-cover rounded-t-lg',
                                        selectedOption === option.key ? 'opacity-100' : 'opacity-50'
                                    ]" 
                                    src="/assets/images/buyflow/free-months-image.jpg" 
                                    alt="Free Month Promotion"
                                />
                                <img 
                                    v-else-if="option.promoType === 'giftcard'" 
                                    :class="[
                                        'w-full h-32 object-cover rounded-t-lg',
                                        selectedOption === option.key ? 'opacity-100 border-blue' : 'opacity-50'
                                    ]" 
                                    src="/assets/images/buyflow/giftcard-image.jpg" 
                                    alt="Gift Card Promotion"
                                /> 
                            </div>
                            
                            <div class="p-4 text-center">
                                <div class="flex justify-center ">
                                    <div class="rounded-md p-2 -mt-12 mb-2 pointer-events-none"
                                    :class="[selectedOption === option.key ? 'bg-salmon' : 'bg-gray-200 group-hover:bg-salmon']">
                                        <img class="star h-6 w-6" src="/assets/images/buyflow/star.svg" alt="star icon"/>
                                    </div>
                                </div>

                                <div v-if="option.promoType === 'free_months'">
                                    <p class="uppercase font-bold">
                                        {{ option.months }} {{ option.months > 1 ? 'months' : 'month' }}  free <br /> internet
                                    </p>
                                    <div class="mt-2">
                                        <a class="underline font-bold" href="#" @click.stop.prevent="showLearnMoreModal(option)">Learn more</a>
                                    </div>
                                </div>

                                <div v-else-if="option.promoType === 'giftcard'">
                                    <p class="uppercase font-bold">
                                        ${{ option.amount }} prepaid <br />gift card
                                    </p>
                                    <div class="mt-2">
                                        <a class="underline font-bold" href="#" @click.stop.prevent="showLearnMoreModal(option)">Learn more</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- If no promotion is applied or no choices available -->
        <div v-else class="no-promo-container">
            <div class="promo-banner bg-salmon p-1 rounded-md">
                <h3 class="text-center text-base font-bold">&#9733; ONLY WITH ULTRAFAST AND GIGAFAST INTERNET PLANS</h3>
            </div>
            
            <div class="p-4 text-center">
                <p class="font-bold mb-2 text-lg">Pick your promo!</p>
                <p class="mb-4">Upgrade to an <b>UltraFast</b> or <b>GigaFast</b> internet plan to get your choice of <b>1 month free internet</b> or a <b>Prepaid gift card</b>.</p>
                <button 
                    @click="upgradeInternet"
                    class="bg-blue text-sm font-bold text-white py-2 px-10 rounded-full hover:bg-blue-light transition-colors">
                    Upgrade Now
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import AppModal from "~/Buyflow/components/AppModal"

export default {
    name: 'GiftChoice',
    components: {
        AppModal 
    },
    data() {
        return {
            footnoteInfo: ''
        }
    },
    props: {
        selectedService: {
            type: Object,
            required: true
        },
        services: {
            type: Object,
            required: true
        }
    },
    computed: {
        ...mapState({
            appliedPromotions: state => state.Promotions.appliedPromotions,
            activePromotions: state => state.Promotions.activePromotions,
            selectedOption: state => state.Promotions.selectedGiftOption,
            showGiftChoicePopup: state => state.Promotions.showGiftChoicePopup
        }),
        ...mapGetters({
            getUiElements: 'Promotions/getUiElements'
        }),
        ...mapGetters({
            multipleChoicePromo: 'Promotions/multipleChoicePromo',
            hasMultipleChoicePromo: 'Promotions/hasMultipleChoicePromo'
        }),
        
        // Get current product from the applied promotion
        currentProduct() {
            if (!this.hasMultipleChoicePromo) return null;
            
            // Find the promotion with type 'multiple'
            const multiplePromo = Object.values(this.appliedPromotions).find(promo => 
                promo.promoType === 'multiple'
            );
            
            return multiplePromo ? multiplePromo.product : null;
        },
        
        // Get available choices based on internet speed selected
        currentChoices() {
            if (!this.multipleChoicePromo || !this.selectedService.speed) {
                console.log('Missing multipleChoicePromo or selectedService.speed');
                return [];
            }
            
            const speed = this.selectedService.speed.download;
            
            const eligibleOptions = this.multipleChoicePromo.gift_options.filter(option => 
                option.eligibleSpeeds.includes(speed)
            );
            
            return eligibleOptions;
        },
        
        getHeaderText() {
            if (this.currentProduct && this.multipleChoicePromo) {
                const productKey = this.currentProduct.productKey
                const headerElement = this.getUiElements('multiple', productKey, 'package_header')
                return headerElement ? headerElement.text : 'Choose your promotion'
            }
            return 'Choose your promotion'
        }
    },
    methods: {
        ...mapActions({
            applyPromotion: 'Promotions/applyPromotion',
            addPromotion: 'Buyflow/addPromotion',
            setSelectedGiftOption: 'Promotions/setSelectedGiftOption',
            setGiftChoicePopup: 'Promotions/setGiftChoicePopup'
        }),  
        showLearnMoreModal(option) {
            this.footnoteInfo = option.cart_footNoteInfo

            this.$nextTick(() => {
                this.$refs.learnMoreModal.show();
            });
        },
        handleModalDismiss() {
          this.setGiftChoicePopup(false);
        },
        // Select a promotion option
        selectOption(optionKey) {
            const currentSpeed = this.selectedService.speed.download
            const selectedOption = this.multipleChoicePromo.gift_options.find(
                option => option.key === optionKey && option.eligibleSpeeds.includes(currentSpeed)
            );

            
            if (selectedOption) {
                this.setSelectedGiftOption(optionKey);
                
                // apply the promotion
                if (selectedOption.WP1Code) {
                    this.addPromotion(selectedOption.WP1Code);
                }

                this.pushEvent('add_to_cart', {
                    'currency': 'USD',
                    'event_label': 'checkout_promo_internet',
                    'items':[
                        {
                            'item_id': selectedOption.WP1Code,
                            'item_name' : selectedOption.name,
                            'item_category': 'promo',
                            'item_brand': 'Breezeline Online',
                            'item_list_name': 'internet promo list',
                            'price': selectedOption.amount,
                            'quantity' : '1'
                        }
                    ],
                    'flow_step_name': 'choose internet and wifi options',
                    'flow_step_number': '1',
                });
            }
        },
        restoreGiftSelection() {
            if (!this.selectedOption || !this.currentChoices.length) return;
            

            const isSelectedOptionAvailable = this.currentChoices.some(
                option => option.key === this.selectedOption
            );
            
            if (isSelectedOptionAvailable) {
                this.selectOption(this.selectedOption);
                return;
            }
            
            // Find previous option type and try to find similar option
            const prevOption = this.multipleChoicePromo?.gift_options.find(
                option => option.key === this.selectedOption
            );
            
            if (prevOption) {
                const match = this.currentChoices.find(
                option => option.promoType === prevOption.promoType
                );
                
                if (match) {
                    this.selectOption(match.key);
                }
            }
        },
        // Upgrade to higher-speed internet
        upgradeInternet() {
            const upgradeService = this.findUltraFastService();
                
            if (upgradeService) {
                // Apply the promotion
                this.applyPromotion({ 
                    offer: {
                        download: upgradeService.speed.download,
                        name: upgradeService.name,
                        title: upgradeService.title
                    },
                    triggerSource: 'siteWide'
                });
                
                // Emit select-options event to change selected service
                this.$emit('select-options', { 
                    service: { 
                        decision: upgradeService.decision, 
                        selection: upgradeService 
                    }
                });

                // Notify parent that service has changed
                this.$emit('changed-selected-service');
            } else {
                console.error('No upgrade service found.');
            }
        },
        findUltraFastService() {
            const availableServices = Object.values(this.services).filter(service => service.isVisible)
            // Find UltraFast service
            return availableServices.find(
                service => service.title && service.title.toLowerCase().includes('ultrafast')
            );
        },
    },
    watch: {
        // When selected service changes, check for eligible promotions
        selectedService: {
            handler(newService) {
                if (newService && newService.speed) {
                    // Apply promotion
                    this.applyPromotion({ 
                        offer: {
                            download: newService.speed.download,
                            name: newService.name,
                            title: newService.title
                        }
                    });
                    
                    // restore gift selection
                    this.$nextTick(() => {
                        setTimeout(() => {
                            this.restoreGiftSelection();
                        }, 0);
                    });
                }
            },
            immediate: true
        },

        showGiftChoicePopup: {
            handler(newVal, oldVal) {
                if (newVal) {
                    this.$nextTick(() => {
                        this.$refs.giftChoiceModal.show();
                    });
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
    .promo-option {
        transition: all 0.2s ease;
    }
    .center-text {
        margin: 0 auto;
        width: 100%;
        max-width: 80%;
        text-align: center;
        margin-bottom: 20px;
    }

    @media screen and (max-width: 768px) {
        .center-text {
            max-width: 100%;
        }
    }

    .promo-option:hover {
        transform: translateY(-2px);
    }
    .promo-option img:hover {
        opacity: 100%;
    }
</style>